import { useCallback, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { milliseconds } from 'date-fns'
import { fetchBirdeyeSearch } from 'apis/birdeye/tokens'

export type SearchResult = {
  amount_24h: number
  latest_price_usd: number
  mint: string
  name: string
  image_url: string | undefined
  symbol: string
  trades_count_24h: number
  volume_24h_usd: number
}

const LIMIT = 10

const fetchSearchResults = async (
  searchString: string,
): Promise<SearchResult[] | string> => {
  try {
    return await fetchBirdeyeSearch(searchString, LIMIT)
  } catch (e) {
    console.error('failed to fetch token search results', e)
    return 'Failed to fetch search results...'
  }
}

export function useSearchResults(searchString: string) {
  const [debouncedSearchString, setDebouncedSearchString] =
    useState(searchString)
  const [loading, setLoading] = useState(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchString = useCallback(
    debounce((value) => {
      setDebouncedSearchString(value)
      setLoading(false)
    }, 300),
    [],
  )

  useEffect(() => {
    setLoading(true)
    debouncedSetSearchString(searchString)
  }, [searchString, debouncedSetSearchString])

  const { data: searchResults, isInitialLoading: loadingSearchResults } =
    useQuery({
      queryKey: ['token-search-results', debouncedSearchString],
      queryFn: () => fetchSearchResults(debouncedSearchString),
      cacheTime: milliseconds({ minutes: 30 }),
      staleTime: milliseconds({ minutes: 30 }),
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: debouncedSearchString.length >= 2,
    })

  return {
    results: searchResults,
    loading: loadingSearchResults || loading,
  }
}
